import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave, FiPrinter, FiCheckCircle } from 'react-icons/fi';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const WarrantyForm = ({ viewOnly = false }) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [showrooms, setShowrooms] = useState([]);
  const [allSalesPersons, setAllSalesPersons] = useState([]);
  const [filteredSalesPersons, setFilteredSalesPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [warrantySN, setWarrantySN] = useState('');
  
  const [formData, setFormData] = useState({
    invoice_no: '',
    model_no: '',
    product_id: '',
    serial_no: '',
    date_of_purchase: '',
    customer_name: '',
    gender: '',
    marital_status: '',
    address: '',
    customer_id: '',
    phone_res: '',
    phone_office: '',
    mobile: '',
    occupation: '',
    education: '',
    showroom_id: '',
    showroom_name: '',
    sales_person_id: '',
    sales_person_name: ''
  });

  useEffect(() => {
    fetchShowrooms();
    fetchSalesPersons();
  }, []);

  useEffect(() => {
    if (isEditMode && allSalesPersons.length > 0 && showrooms.length > 0) {
      fetchWarrantyForEdit();
    }
  }, [isEditMode, allSalesPersons, showrooms]);

  // Filter sales persons when showroom changes or when allSalesPersons is updated
  useEffect(() => {
    if (formData.showroom_id) {
      const filtered = allSalesPersons.filter(
        person => person.showroom_id === parseInt(formData.showroom_id)
      );
      setFilteredSalesPersons(filtered);
      
      // Reset sales_person_id if current selection doesn't belong to filtered list
      if (formData.sales_person_id) {
        const stillValid = filtered.some(p => p.id === parseInt(formData.sales_person_id));
        if (!stillValid) {
          setFormData(prev => ({ ...prev, sales_person_id: '', sales_person_name: '' }));
        }
      }
    } else {
      setFilteredSalesPersons(allSalesPersons);
    }
  }, [formData.showroom_id, allSalesPersons]);

  // Update showroom name when showroom changes
  useEffect(() => {
    if (formData.showroom_id) {
      const showroom = showrooms.find(s => s.id === parseInt(formData.showroom_id));
      if (showroom) {
        setFormData(prev => ({ ...prev, showroom_name: showroom.name }));
      }
    }
  }, [formData.showroom_id, showrooms]);

  // Update sales person name when sales_person changes
  useEffect(() => {
    if (formData.sales_person_id) {
      const person = filteredSalesPersons.find(p => p.id === parseInt(formData.sales_person_id));
      if (person) {
        setFormData(prev => ({ ...prev, sales_person_name: person.name }));
      }
    }
  }, [formData.sales_person_id, filteredSalesPersons]);

  const token = localStorage.getItem('token');
  const authConfig = { headers: { Authorization: `Bearer ${token}` } };

  const fetchWarrantyForEdit = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/warranty-registrations/${id}`,config);
      let data = response.data;
      console.log(response.data);
      if (data.warrantyRegistration) data = data.warrantyRegistration;
      else if (data.data) data = data.data;

      setFormData({
        invoice_no: data.invoice_no || '',
        model_no: data.model_no || '',
        product_id: data.product_id || '',
        serial_no: data.serial_no || '',
        date_of_purchase: data.date_of_purchase ? data.date_of_purchase.split('T')[0] : '',
        customer_name: data.customer_name || '',
        gender: data.gender || '',
        marital_status: data.marital_status || '',
        address: data.address || '',
        customer_id: data.customer_id || '',
        phone_res: data.phone_res || '',
        phone_office: data.phone_office || '',
        mobile: data.mobile || '',
        occupation: data.occupation || '',
        education: data.education || '',
        showroom_id: data.showroom_id ? String(data.showroom_id) : '',
        showroom_name: data.showroom_name || '',
        sales_person_id: data.sales_person_id ? String(data.sales_person_id) : '',
        sales_person_name: data.sales_person_name || '',
      });
    } catch (error) {
      console.error('Error fetching warranty for edit:', error);
      toast.error('Failed to load warranty data');
    }
  };

  const parseList = (data, key = null) => {
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (key && data?.[key] && Array.isArray(data[key])) return data[key];
    if (data?.users && Array.isArray(data.users)) return data.users;
    return [];
  };

  const fetchShowrooms = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/showrooms`, authConfig);
      const showroomsData = parseList(response.data, 'showrooms');
      setShowrooms(showroomsData);
    } catch (error) {
      console.error('Error fetching showrooms:', error);
      toast.error('Failed to load showrooms');
    }
  };

  const fetchSalesPersons = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/sales-persons`, authConfig);
      const salesPersonsData = parseList(response.data, 'data');
      setAllSalesPersons(salesPersonsData);
      setFilteredSalesPersons(salesPersonsData); // Initially show all
    } catch (error) {
      console.error('Error fetching sales persons:', error);
      toast.error('Failed to load sales persons');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.customer_name) {
      toast.error('Customer name is required');
      return;
    }
    if (!formData.mobile) {
      toast.error('Mobile number is required');
      return;
    }
    if (!formData.showroom_id) {
      toast.error('Please select a showroom');
      return;
    }
    
    setLoading(true);

    // Prepare data for submission
    const submitData = {
      ...formData,
      showroom_id: formData.showroom_id ? parseInt(formData.showroom_id) : null,
      sales_person_id: formData.sales_person_id ? parseInt(formData.sales_person_id) : null,
    };

    try {
      if (isEditMode) {
        await axios.put(`${API_BASE_URL}/api/warranty-registrations/${id}`, submitData, authConfig);
        toast.success('Warranty updated successfully!');
        setSubmitted(true);
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/warranty-registrations`, submitData, authConfig);
        setWarrantySN(response.data.warranty_sn || response.data.data?.warranty_sn);
        setSubmitted(true);
        toast.success('Warranty registered successfully!');
        setTimeout(() => {
          resetForm();
        }, 3000);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || (isEditMode ? 'Update failed' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_no: '',
      model_no: '',
      product_id: '',
      serial_no: '',
      date_of_purchase: '',
      customer_name: '',
      gender: '',
      marital_status: '',
      address: '',
      customer_id: '',
      phone_res: '',
      phone_office: '',
      mobile: '',
      occupation: '',
      education: '',
      showroom_id: '',
      showroom_name: '',
      sales_person_id: '',
      sales_person_name: ''
    });
    setSubmitted(false);
    setWarrantySN('');
    // Reset filters to show all sales persons
    setFilteredSalesPersons(allSalesPersons);
  };

  const printWarranty = () => {
    window.print();
  };

  if (submitted) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center">
          <div className="card max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-syakar-navy mb-4">
              {isEditMode ? 'Warranty Updated Successfully!' : 'Warranty Registered Successfully!'}
            </h2>
            {!isEditMode && (
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-600">Your Warranty SN:</p>
                <p className="text-xl font-bold text-syakar-red">{warrantySN}</p>
              </div>
            )}
            <p className="text-gray-600 mb-6">
              {isEditMode ? 'The warranty record has been updated.' : 'Please save this warranty number for future reference.'}
            </p>
            <div className="flex gap-4">
              {isEditMode ? (
                <button onClick={() => navigate('/admin/warranties')} className="btn-primary flex-1">
                  Back to List
                </button>
              ) : (
                <button onClick={resetForm} className="btn-primary flex-1">
                  Register New
                </button>
              )}
              <button onClick={printWarranty} className="btn-secondary flex-1 flex items-center justify-center gap-2">
                <FiPrinter /> Print
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="page-header">{viewOnly ? 'View Warranty' : isEditMode ? 'Edit Warranty' : 'Create Warranty'}</h1>
          <p className="text-gray-500 mt-1 text-sm">{viewOnly ? `Warranty #${id}` : isEditMode ? `Editing warranty #${id}` : 'Please fill in all required information'}</p>
        </div>

        <form onSubmit={handleSubmit} className="card">
          <fieldset disabled={viewOnly} className="contents">
          {/* Product Information Section */}
          <div className="mb-8">
            <h2 className="section-title">
              Product Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice No
                </label>
                <input
                  type="text"
                  name="invoice_no"
                  value={formData.invoice_no}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model No
                </label>
                <input
                  type="text"
                  name="model_no"
                  value={formData.model_no}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product ID
                </label>
                <input
                  type="text"
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Serial No
                </label>
                <input
                  type="text"
                  name="serial_no"
                  value={formData.serial_no}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Purchase
                </label>
                <input
                  type="date"
                  name="date_of_purchase"
                  value={formData.date_of_purchase}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Showroom/Dealer Information */}
          <div className="mb-8">
            <h2 className="section-title">
              Showroom/Dealer Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Showroom/Dealer *
                </label>
                <select
                  name="showroom_id"
                  value={formData.showroom_id}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Select Showroom</option>
                  {showrooms.map(showroom => (
                    <option key={showroom.id} value={showroom.id}>
                      {showroom.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sales Person
                </label>
                <select
                  name="sales_person_id"
                  value={formData.sales_person_id}
                  onChange={handleChange}
                  className="input-field"
                  disabled={!formData.showroom_id}
                >
                  <option value="">Select Sales Person</option>
                  {filteredSalesPersons.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
                </select>
                {formData.showroom_id && filteredSalesPersons.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    No sales persons assigned to this showroom
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h2 className="section-title">
              Customer Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marital Status
                </label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer ID
                </label>
                <input
                  type="text"
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone (Res)
                </label>
                <input
                  type="tel"
                  name="phone_res"
                  value={formData.phone_res}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone (Office)
                </label>
                <input
                  type="tel"
                  name="phone_office"
                  value={formData.phone_office}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile *
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Occupation
                </label>
                <input
                  type="text"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education
                </label>
                <input
                  type="text"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          </fieldset>

          {viewOnly ? (
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/admin/warranties')}
                className="btn-secondary flex-1"
              >
                Back to List
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/warranty/print/${id}`)}
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                <FiPrinter size={16} /> Print
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/warranty/edit/${id}`)}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                Edit Warranty
              </button>
            </div>
          ) : (
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? (isEditMode ? 'Updating...' : 'Registering...') : (isEditMode ? 'Update Warranty' : 'Register Warranty')}
                <FiSave size={18} />
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary flex-1"
              >
                Clear Form
              </button>
            </div>
          )}
        </form>
      </div>
    </AdminLayout>
  );
};

export default WarrantyForm;